import React, { useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import '@ica-azure/ica-elements/IcaDialog'

type IcaDialogProps = React.JSX.IntrinsicElements['tgdb-ica-dialog']

type IcaDialog = IcaDialogProps & {
  onClose?: (e: CustomEvent) => void
  onClosed?: (e: CustomEvent) => void
  onMinimize?: (e: CustomEvent) => void
  onOpen?: (e: CustomEvent) => void
}

export function IcaDialog({
  noresize,
  snaptotop,
  open,
  desktopcustomheight,
  desktopfullheight,
  inactivebottompercentage,
  children,
  ...rest
}: IcaDialog) {
  const dRef = useRef<HTMLDialogElement>(null)
  const portal = document.getElementById('modal-portal')

  useEffect(() => {
    const current = dRef.current
    if (current) {
      if (rest.onClosed) {
        current.addEventListener('closed', rest.onClosed as EventListener)
      }
      if (rest.onClose) {
        current.addEventListener('close', rest.onClose as EventListener)
      }
      if (rest.onMinimize) {
        current.addEventListener('minimize', rest.onMinimize as EventListener)
      }

      return () => {
        if (rest.onClosed) {
          current.removeEventListener('closed', rest.onClosed as EventListener)
        }
        if (rest.onClose) {
          current.removeEventListener('close', rest.onClose as EventListener)
        }
        if (rest.onMinimize) {
          current.removeEventListener(
            'minimize',
            rest.onMinimize as EventListener
          )
        }
      }
    }
  }, [rest.onClosed, rest.onClose, rest.onMinimize])

  useEffect(() => {
    if (open) {
      onOpenDialog()
    } else {
      onCloseDialog()
    }
  }, [open])

  function onOpenDialog() {
    if (typeof dRef?.current?.show === 'function') {
      dRef?.current?.show()
    }
  }

  function onCloseDialog() {
    if (typeof dRef?.current?.close === 'function') {
      dRef?.current?.close()
    }
  }

  /* Workaround to make the overlay darker and prevent backdrop close */
  useEffect(() => {
    const dialog = dRef.current
    if (!dialog) return
  
    let stopClick: ((e: MouseEvent) => void) | null = null
    let overlay: HTMLElement | null = null
  
    const applyOverlayModifications = () => {
      const shadow = dialog.shadowRoot
      overlay = shadow?.querySelector('.IcaDialog-ie__overlay') as HTMLElement
  
      // Make overlay darker than default
      if (overlay) {
        overlay.style.background = 'rgba(0, 0, 0, 0.65)'
      }
  
    }
  
    const onOpened = () => {
      applyOverlayModifications()
    }
  
    dialog.addEventListener('opened', onOpened)
  
    if (open) {
      setTimeout(() => {
        applyOverlayModifications()
      }, 50)
    }
  
    return () => {
      dialog.removeEventListener('opened', onOpened)
      if (overlay && stopClick) {
        overlay.removeEventListener('click', stopClick, true)
      }
    }
  }, [open])

  return (
    portal &&
    ReactDOM.createPortal(
      <tgdb-ica-dialog
        ref={dRef}
        noresize={noresize}
        snaptotop={snaptotop}
        open={open}
        desktopcustomheight={desktopcustomheight}
        desktopfullheight={desktopfullheight}
        inactivebottompercentage={inactivebottompercentage}
        {...rest}
      >
        {children}
      </tgdb-ica-dialog>,
      portal
    )
  )
}
